import axios from 'axios';
import getBrowserId from '@/services/BrowserIdentifier';
import AuthCookieHandler from '@/services/AuthCookieHandler';

const axiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_SEAT_BASE_URL}/v${import.meta.env.VITE_API_VERSION}`,
  timeout: 1000 * 10,
});

const publicAxiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_SEAT_BASE_URL}/v${import.meta.env.VITE_API_VERSION}`,
  timeout: 1000 * 10,
});

const authAxiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_AUTH_BASE_URL}/v${import.meta.env.VITE_API_VERSION}`,
  timeout: 1000 * 10,
});

const refreshAxiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_AUTH_BASE_URL}/v${import.meta.env.VITE_API_VERSION}`,
  timeout: 1000 * 10,
});

const isInAdminSection = (path: string): boolean => {
  const adminQrPattern = /\/qr\/[^/]+\/admin(?!-login)($|\/)/;
  return adminQrPattern.test(path);
};

const hasAccessToken = (): boolean => {
  const authHandler = new AuthCookieHandler();
  try {
    const token = authHandler.getAccessToken();
    return !!token;
  } catch {
    return false;
  }
};

const handleAdminLogout = () => {
  const authHandler = new AuthCookieHandler();
  authHandler.removeAuth();
  console.log('🔓 User left admin section - logged out');
};

const checkAndClearAuthIfNeeded = () => {
  const currentPath = window.location.pathname;
  const isCurrentlyInAdmin = isInAdminSection(currentPath);

  if (!isCurrentlyInAdmin && hasAccessToken()) {
    handleAdminLogout();
  }
};

if (typeof window !== 'undefined') {
  let lastPath = window.location.pathname;

  window.addEventListener('popstate', () => {
    checkAndClearAuthIfNeeded();
  });

  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function (...args) {
    originalPushState.apply(this, args);
    checkAndClearAuthIfNeeded();
  };

  history.replaceState = function (...args) {
    originalReplaceState.apply(this, args);
    checkAndClearAuthIfNeeded();
  };

  setInterval(() => {
    if (lastPath !== window.location.pathname) {
      checkAndClearAuthIfNeeded();
      lastPath = window.location.pathname;
    }
  }, 500);
}

authAxiosInstance.interceptors.request.use(
  async (config) => {
    config.headers['x-device-identifier'] = getBrowserId().toString();
    return config;
  },
  (error) => Promise.reject(error),
);

publicAxiosInstance.interceptors.request.use(
  async (config) => {
    config.headers['x-device-identifier'] = getBrowserId().toString();
    config.headers['Accept'] = 'application/json';
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.request.use(
  async (config) => {
    const authHandler = new AuthCookieHandler();
    try {
      const token = authHandler.getAccessToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      } else {
        console.warn('⚠️ No token found for:', config.url);
      }
    } catch (error) {
      console.warn('❌ Error getting token:', error);
    }
    config.headers['x-device-identifier'] = getBrowserId().toString();
    config.headers['Accept'] = 'application/json';
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }
    return config;
  },
  (error) => Promise.reject(error),
);

refreshAxiosInstance.interceptors.request.use(
  async (config) => {
    config.headers['x-device-identifier'] = getBrowserId().toString();
    config.headers['Content-Type'] = 'application/x-www-form-urlencoded';
    return config;
  },
  (error) => Promise.reject(error),
);

refreshAxiosInstance.interceptors.response.use(
  (response) => {
    if (!response || !response.data) {
      console.error('Invalid response structure:', response);
      throw new Error('Invalid API response');
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (!response || !response.data) {
      console.error('Invalid response structure:', response);
      throw new Error('Invalid API response');
    }
    return response;
  },
  async function (error) {
    if (error.response?.status === 401) {
      console.log('❌ 401 Unauthorized - Logging out immediately');
      const authHandler = new AuthCookieHandler();
      authHandler.removeAuth();

      const pathParts = window.location.pathname.split('/');
      const qrIndex = pathParts.indexOf('qr');
      const venueId = qrIndex !== -1 ? pathParts[qrIndex + 1] : null;
      const redirectPath = venueId ? `/qr/${venueId}/admin-login` : '/login';

      console.log('🔀 Redirecting to:', redirectPath);
      await new Promise((resolve) => setTimeout(resolve, 100));
      window.location.href = redirectPath;

      return new Promise(() => {});
    }

    return Promise.reject(error);
  },
);

authAxiosInstance.interceptors.response.use(
  (response) => {
    if (!response || !response.data) {
      console.error('Invalid response structure:', response);
      throw new Error('Invalid API response');
    }
    return response;
  },
  async function (error) {
    const originalUrl = error.config?.url || '';
    const isLoginRequest = originalUrl.includes('/auth/business-user-login');

    if (isLoginRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 403) {
      const originalUrl = error.config?.url || '';

      if (originalUrl.includes('/auth/business-user-login')) {
        return Promise.reject(error);
      }

      const authHandler = new AuthCookieHandler();
      authHandler.removeAuth();

      localStorage.setItem('login_403_redirect', 'true');

      const pathParts = window.location.pathname.split('/');
      const qrIndex = pathParts.indexOf('qr');
      const venueId = qrIndex !== -1 ? pathParts[qrIndex + 1] : null;
      const redirectPath = venueId ? `/qr/${venueId}/admin-login` : '/';
      localStorage.setItem('redirect_path', redirectPath);

      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

publicAxiosInstance.interceptors.response.use(
  (response) => {
    if (!response || !response.data) {
      console.error('Invalid response structure:', response);
      throw new Error('Invalid API response');
    }
    return response;
  },
  (error) => Promise.reject(error),
);

class APIClient<T> {
  endpoint: string;
  pagination: boolean;
  isPublic: boolean;

  constructor(
    baseService: string,
    endpoint: string,
    pagination: boolean = false,
    isPublic: boolean = false,
  ) {
    this.endpoint = `${baseService}/v${import.meta.env.VITE_API_VERSION}${endpoint}`;
    this.pagination = pagination;
    this.isPublic = isPublic;
  }

  private getAxiosInstance() {
    return this.isPublic ? publicAxiosInstance : axiosInstance;
  }

  get = async (params?: Record<string, unknown>): Promise<T> => {
    try {
      const instance = this.getAxiosInstance();
      const res = await instance.get<T>(this.endpoint, {
        params: {
          ...(params ? params : {}),
        },
      });

      if (!res || !res.data) {
        console.error('Invalid response from API:', res);
      }

      return res.data;
    } catch (error) {
      console.error('API get error:', error);
      throw error;
    }
  };

  getList = async (params?: Record<string, unknown>): Promise<T[]> => {
    try {
      const instance = this.getAxiosInstance();
      const res = await instance.get<T[]>(this.endpoint, {
        params: {
          ...(this.pagination ? { pagination: 'false' } : {}),
          ...(params ? params : {}),
        },
      });

      if (!res || !res.data) {
        console.error('Invalid response from API:', res);
      }

      return res.data;
    } catch (error) {
      console.error('API getList error:', error);
      throw error;
    }
  };

  getFile = async () => {
    const instance = this.getAxiosInstance();
    const res = await instance.get<T>(this.endpoint, {
      responseType: 'blob',
    });
    return res.data;
  };

  post = async <P>(data: T) => {
    const instance = this.getAxiosInstance();
    const res = await instance.post<P>(this.endpoint, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res.data;
  };

  auth = <P>(data: T) => {
    return authAxiosInstance
      .post<P>(this.endpoint, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((res) => res.data);
  };

  update = <P>(data: T) => {
    const instance = this.getAxiosInstance();
    return instance
      .patch<P>(this.endpoint, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((res) => res.data);
  };
}

export default APIClient;
export { publicAxiosInstance };
